/* The server's span kinds own the timeline. Compatibility rides supply only boundary details. */
const at = (boundary) => (typeof boundary === 'string' ? boundary : boundary && boundary.ts);

function rideIdentity(ride) {
  return at(ride.start) || at(ride.end) || null;
}

function rideBoundarySide(ride) {
  return at(ride.start) ? 'start' : 'end';
}

function ordered(rows) {
  const start = (row) => {
    if (!row.start) { return -Infinity; }
    return new Date(at(row.start)).getTime();
  };
  return rows.slice().sort((a, b) => start(a) - start(b));
}

function durationOf(start, end) {
  return start && end ? (new Date(at(end)) - new Date(at(start))) / 1000 : null;
}

function matchingRide(span, rides) {
  // Exact pairs disambiguate a shared end/start. A clipped start uses its end as identity,
  // just as the selection endpoint does; never pair lists by their position.
  const exact = rides.find((ride) => (at(ride.start) || null) === span.start
    && (at(ride.end) || null) === span.end);
  if (exact) { return exact; }
  if (span.start) {
    return rides.find((ride) => at(ride.start) === span.start);
  }
  return span.end ? rides.find((ride) => at(ride.end) === span.end) : null;
}

function boundary(ts, held, sample) {
  if (!ts) { return null; }
  const source = (held && held.ts === ts ? held : null) || sample || {};
  return {
    ts,
    lat: source.lat === undefined ? null : source.lat,
    lng: source.lng === undefined ? null : source.lng,
    odometer_km: source.odometer_km === undefined ? null : source.odometer_km,
    provenance: source.provenance || 'unknown',
    place_name: typeof source.place_name === 'string' ? source.place_name : null,
  };
}

function emptyHistoryState(data) {
  const days = data.days;
  const states = days && typeof days === 'object' && !Array.isArray(days)
    ? Object.keys(days).map((day) => days[day]) : [];
  // The legacy "moved" enum means any recorded observation, not proven driving. Reduce
  // every served Helsinki day, not the browser date or the separate month coverage cache.
  if (states.indexOf('moved') !== -1) { return 'moved'; }
  return states.length > 0 && states.every((state) => state === 'still') && !data.truncated
    ? 'still' : 'unknown';
}

function normaliseHistory(data) {
  const compatible = data.rides || [];
  const canonical = Array.isArray(data.spans);
  let rows;
  if (canonical) {
    rows = data.spans.map((span) => {
      const seconds = durationOf(span.start, span.end);
      if (span.kind !== 'ride') {
        return { ...span, canonical: true, duration_seconds: seconds };
      }
      const held = matchingRide(span, compatible) || {};
      const samples = span.samples || [];
      return {
        ...held,
        kind: 'ride', canonical: true,
        start: boundary(span.start, held.start, samples[0]),
        end: boundary(span.end, held.end, samples[samples.length - 1]),
        start_provenance: span.start_provenance,
        end_provenance: span.end_provenance,
        distance_km: span.distance_m === null || span.distance_m === undefined
          ? null : span.distance_m / 1000,
        duration_seconds: seconds,
        open: !!span.open, partial: !!span.partial,
        // Keep fixless readings for telemetry. Only the map filters unpositioned samples.
        samples,
      };
    });
  } else {
    rows = [];
    ordered(compatible).forEach((ride, index, rides) => {
      const previous = rides[index - 1];
      if (previous && previous.end && ride.start
        && new Date(ride.start.ts) > new Date(previous.end.ts)) {
        rows.push({
          kind: 'discontinuity', start: previous.end.ts, end: ride.start.ts,
          distance_m: null, canonical: false,
        });
      }
      rows.push({ ...ride, kind: 'ride', canonical: false });
    });
  }
  rows = ordered(rows);
  return {
    rows,
    rides: rows.filter((row) => row.kind === 'ride'),
    canonical,
    // Fewer than two observations can produce no spans. Empty rows alone cannot prove a
    // quiet day, and this disclosure must not manufacture a journey or a missing period.
    emptyState: rows.length === 0 ? emptyHistoryState(data) : null,
    // A trailing silence has no later sample to form a gap span. Its unproven boundary
    // still means missing observations, so the sum is only a recorded subtotal.
    incomplete: !canonical || !!data.truncated
      || rows.some((row) => hasUnprovenBoundary(row)
        || (row.kind !== 'ride' && row.kind !== 'stop')),
  };
}

function hasUnprovenBoundary(ride) {
  return ride.start_provenance === 'unproven' || ride.end_provenance === 'unproven';
}

function hasPosition(point) {
  return !!point && Number.isFinite(point.lat) && Number.isFinite(point.lng)
    && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180;
}

function boundaryLabel(point, name) {
  if (name) { return name; }
  if (!hasPosition(point)) { return '—'; }
  return point.lat.toFixed(4) + ', ' + point.lng.toFixed(4);
}

function cachedBoundaryNames(ride) {
  const names = {};
  ['start', 'end'].forEach((side) => {
    names[side] = ride[side] && typeof ride[side].place_name === 'string'
      ? ride[side].place_name : null;
  });
  return names;
}

function placeNamesKey(vehicleId, window, ride) {
  const identity = rideIdentity(ride);
  return window && identity
    ? JSON.stringify([
      String(vehicleId), window.from, window.to, rideBoundarySide(ride) + ':' + identity,
    ]) : null;
}

export {
  normaliseHistory, hasUnprovenBoundary, hasPosition, boundaryLabel,
  cachedBoundaryNames, placeNamesKey, rideIdentity, rideBoundarySide,
};
